import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Trophy, Medal, Share2, X, Copy, Check } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import Confetti from 'react-confetti';
import { Footer } from "./layout/Footer";
import { getPlayerIconById } from "../lib/playerIcons";

interface Player {
  player_name: string;
  score: number;
  is_spy: boolean;
  icon_id?: number;
}

export default function WinnersPodium() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentDeck = location.state?.currentDeck || 'locationDeck';
  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showShareBanner, setShowShareBanner] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bannerHeight, setBannerHeight] = useState("max-h-16");
  const [windowDimensions, setWindowDimensions] = useState({ 
    width: window.innerWidth,
    height: window.innerHeight 
  });

  // Update window dimensions when window is resized
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
      
      // Update banner height based on screen width
      if (window.innerWidth < 480) {
        setBannerHeight("max-h-28");
      } else if (window.innerWidth < 640) {
        setBannerHeight("max-h-24");
      } else {
        setBannerHeight("max-h-16");
      }
    };

    // Initial setting
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Start confetti after 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Show share banner after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowShareBanner(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await fetch(`/api/rooms/${roomId}/players`);
        if (!response.ok) {
          throw new Error('Failed to fetch players');
        }
        const data = await response.json();
        // Sort players by score in descending order
        const sortedPlayers = data.sort((a: Player, b: Player) => b.score - a.score);
        setPlayers(sortedPlayers);
      } catch (err) {
        setError('Failed to load players');
        console.error('Error fetching players:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlayers();
  }, [roomId]);

  const handlePlayAgain = () => {
    navigate('/app');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText("https://spyfall.co")
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // Function to render player icon
  const renderPlayerIcon = (iconId?: number, size: 'small' | 'medium' | 'large' = 'small') => {
    if (!iconId && iconId !== 0) return null;
    
    const icon = getPlayerIconById(iconId);
    const sizeClasses = {
      small: "h-5 w-5",
      medium: "h-8 w-8",
      large: "h-12 w-12",
    };
    
    return (
      <div 
        className={`${sizeClasses[size]} inline-flex items-center justify-center`}
        dangerouslySetInnerHTML={{ __html: icon.svg }}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Loading results...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl text-red-500">{error}</div>
      </div>
    );
  }

  // Get top 3 players for the podium
  const topThree = players.slice(0, 3);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col overflow-x-hidden w-full relative">
      {showConfetti && (
        <Confetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          numberOfPieces={900}
          recycle={false}
          colors={['#ef4444', '#eab308', '#22c55e', '#3b82f6', '#d946ef']}
        />
      )}
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-gray-800 bg-black/80 backdrop-blur-sm">
        <div className="w-full px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => window.location.replace('/')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-xl font-bold text-red-500">SPYFALL</span>
            </button>
          </div>
          <Button
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            onClick={handlePlayAgain}
          >
            Play Again
          </Button>
        </div>
      </header>

      {/* Share banner */}
      <div 
        className={`relative w-full border-b border-gray-800 bg-gradient-to-r from-red-900/80 to-black/80 backdrop-blur-sm transition-all duration-700 ease-in-out overflow-hidden ${
          showShareBanner ? `${bannerHeight} py-2` : 'max-h-0 py-0'
        }`}
      >
        <div className="w-full px-2 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center">
          <div className="flex items-center gap-2 justify-center py-1 sm:py-0">
            <Share2 className="h-5 w-5 text-red-400 flex-shrink-0" />
            <span className="text-xs sm:text-sm md:text-base text-center">
              Enjoyed the game? Please support Mr Spy{' '} 
              <a 
                href="https://buymeacoffee.com/spyfall" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Buy Him a Pizza Slice 🍕
              </a>
              {' '}!
            </span>
          </div>
          <div className="flex items-center justify-center sm:ml-3 mt-1 sm:mt-0 sm:gap-3">
            <button
              onClick={copyToClipboard}
              className="px-2 py-1 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors flex items-center gap-1 flex-shrink-0 min-w-0"
              aria-label="Copy link to clipboard"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-green-400" />
                  <span className="text-xs whitespace-nowrap text-green-400">Thanks!</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 text-gray-300" />
                  <span className="text-xs whitespace-nowrap">Copy Link</span>
                </>
              )}
            </button>
            <button 
              onClick={() => setShowShareBanner(false)}
              className="p-1 ml-2 sm:ml-0 rounded-full hover:bg-gray-700/50 flex-shrink-0"
              aria-label="Close banner"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <main className="flex-grow w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-8 text-center">Game Results</h1>

          {topThree.length > 0 && (
            <>
              {/* Winner announcement */}
              <div className="text-center mb-12">
                <h2 className="text-2xl font-bold text-red-500">{topThree[0].player_name} Wins!</h2>
                <p className="text-gray-400 mt-2">
                  {currentDeck === 'animalsDeck' ? 'Well well well, looks like we have a master hunter!' : 'Well well well, looks like we have ex CIA agent!'}
                </p>
              </div>

              {/* Podium section */}
              <div className="relative h-64 mb-16 px-2">
                {/* Decorative circles */}
                <div className="absolute w-full h-full pointer-events-none">
                  <div className="absolute -left-4 top-1/3 w-12 h-12 rounded-full border-2 border-blue-500 opacity-20"></div>
                  <div className="absolute left-1/4 -top-4 w-8 h-8 rounded-full border-2 border-red-500 opacity-20"></div>
                  <div className="absolute right-1/4 top-1/2 w-16 h-16 rounded-full border-2 border-amber-500 opacity-20"></div>
                  <div className="absolute -right-2 top-10 w-10 h-10 rounded-full border-2 border-blue-500 opacity-20"></div>
                </div>
                
                <div className="flex justify-center items-end h-full gap-1 sm:gap-3 relative">
                  {/* Second Place */}
                  {topThree.length > 1 && (
                    <div className="w-24 sm:w-28 flex flex-col items-center relative">
                      <div className="bg-gray-700 rounded-t-md p-2 w-full text-center">
                        <Medal className="h-5 w-5 sm:h-6 sm:w-6 text-gray-300 mx-auto mb-1" />
                        <p className="font-bold text-sm sm:text-base truncate px-1">{topThree[1].player_name}</p>
                        <p className="text-xs sm:text-sm text-gray-300">{topThree[1].score} pts</p>
                      </div>
                      <div className="bg-gray-600 h-24 sm:h-28 w-full rounded-b-md flex items-end justify-center relative">
                        <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
                          <div className="relative">
                            <div className="w-14 sm:w-16 h-14 sm:h-16 rounded-full bg-blue-900 flex items-center justify-center">
                              <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-full border-2 border-gray-300 bg-transparent flex items-center justify-center overflow-hidden">
                                {renderPlayerIcon(topThree[1].icon_id, 'medium')}
                              </div>
                            </div>
                            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-7 sm:w-8 h-7 sm:h-8 rounded-full bg-blue-900 border-2 border-gray-300 flex items-center justify-center text-white font-bold text-lg">
                              2
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* First Place */}
                  <div className="w-24 sm:w-28 flex flex-col items-center z-10 relative">
                    <div className="bg-red-700 rounded-t-md p-2 w-full text-center">
                      <Trophy className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-300 mx-auto mb-1" />
                      <p className="font-bold text-sm sm:text-base truncate px-1">{topThree[0].player_name}</p>
                      <p className="text-xs sm:text-sm text-gray-200">{topThree[0].score} pts</p>
                    </div>
                    <div className="bg-red-600 h-36 sm:h-40 w-full rounded-b-md flex items-end justify-center relative">
                      <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
                        <div className="relative">
                          <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full bg-red-900 flex items-center justify-center">
                            <div className="w-14 sm:w-18 h-14 sm:h-18 rounded-full border-2 border-yellow-400 bg-transparent flex items-center justify-center overflow-hidden">
                              {renderPlayerIcon(topThree[0].icon_id, 'large')}
                            </div>
                          </div>
                          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-7 sm:w-8 h-7 sm:h-8 rounded-full bg-red-900 border-2 border-yellow-400 flex items-center justify-center text-white font-bold text-lg">
                            1
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Third Place */}
                  {topThree.length > 2 && (
                    <div className="w-24 sm:w-28 flex flex-col items-center relative">
                      <div className="bg-gray-700 rounded-t-md p-2 w-full text-center">
                        <Medal className="h-5 w-5 sm:h-6 sm:w-6 text-amber-700 mx-auto mb-1" />
                        <p className="font-bold text-sm sm:text-base truncate px-1">{topThree[2].player_name}</p>
                        <p className="text-xs sm:text-sm text-gray-300">{topThree[2].score} pts</p>
                      </div>
                      <div className="bg-gray-600 h-16 sm:h-20 w-full rounded-b-md flex items-end justify-center relative">
                        <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
                          <div className="relative">
                            <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-full bg-amber-900 flex items-center justify-center">
                              <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-full border-2 border-amber-600 bg-transparent flex items-center justify-center overflow-hidden">
                                {renderPlayerIcon(topThree[2].icon_id, 'medium')}
                              </div>
                            </div>
                            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-7 sm:w-8 h-7 sm:h-8 rounded-full bg-amber-900 border-2 border-amber-600 flex items-center justify-center text-white font-bold text-lg">
                              3
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Leaderboard */}
          <div className="mx-auto w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Leaderboard</h3>
            <div className="bg-gray-800 rounded-lg overflow-hidden overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="py-3 px-4 sm:px-6 text-left">Position</th>
                    <th className="py-3 px-4 sm:px-6 text-left">Player</th>
                    <th className="py-3 px-4 sm:px-6 text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((player, index) => {
                    return (
                      <tr 
                        key={player.player_name} 
                        className={cn(
                          "border-b border-gray-700 last:border-0",
                          index < 3 ? "bg-gray-700/30" : ""
                        )}
                      >
                        <td className="py-3 px-4 sm:px-6 font-medium">
                          {index === 0 ? (
                            <span className="flex items-center text-yellow-300">
                              <Trophy className="h-4 w-4 mr-1" /> {index + 1}
                            </span>
                          ) : index < 3 ? (
                            <span className="flex items-center text-gray-300">
                              <Medal className="h-4 w-4 mr-1" /> {index + 1}
                            </span>
                          ) : (
                            index + 1
                          )}
                        </td>
                        <td className="py-3 px-4 sm:px-6">
                          <div className="flex items-center">
                            {player.icon_id !== undefined && (
                              <div className="w-7 h-7 rounded-full bg-gray-700 mr-2 flex items-center justify-center">
                                {renderPlayerIcon(player.icon_id)}
                              </div>
                            )}
                            <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                              {player.player_name}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 sm:px-6 text-right">{player.score}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
} 